import React, { useState, useMemo, useEffect, useRef } from "react";
import PageLayout from "../../Layouts/PageLayout/PageLayout";
import Table from "../../components/common/Table/Table";
import Button from "../../components/common/Button/Button";
import { FiDownload } from "react-icons/fi";
import "./ObrasMenores.scss";
import { getApiInstance } from "../../utils/api";
import { nombreFicheroDescarga } from "../../utils/descargas";
import { CiCircleCheck, CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import { IoStopOutline, IoAdd } from "react-icons/io5";
import { useZone } from "../../context/ZoneContext";
import { ZONE_LABEL_MAP } from "../../constants/zones";
import { mapList } from "../../utils/utils";
import { useTranslation } from "react-i18next";

export default function ObrasMenores({ user }) {
    const { t } = useTranslation();
    const tableRef = useRef(null);
    const canAdd = Number(user.codAcceso) === 1 && Number(user.role) !== 4;
    const [obrasMenores, setObrasMenores] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { selectedZone } = useZone();
    const [productores, setProductores] = useState([]);
    const [comunidades, setComunidades] = useState([]);
    const [paises, setPaises] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [cadenas, setCadenas] = useState([]);
    const [selectedRows, setSelectedRows] = useState(new Set());

    const columnasObrasMenores = useMemo(
        () => [
            { key: "codObraMenor", label: "ID", visible: false },
            {
                key: "nombre",
                label: t("minorWorks.columns.centerCode"),
                visible: true,
                sortable: true,
                isLink: true,
                width: "130px",
            },
            {
                key: "localidad",
                label: t("fields.locality"),
                visible: true,
                sortable: true,
                width: "200px",
            },
            {
                key: "fechaFin",
                label: t("fields.endDate"),
                visible: true,
                sortable: true,
                type: "date",
                width: "120px",
            },
            {
                key: "intermediario",
                label: t("minorWorks.columns.intermediaryCompany"),
                visible: true,
                sortable: true,
                width: "180px",
            },
            {
                key: "cadena",
                label: t("fields.chain"),
                visible: true,
                sortable: true,
                width: "120px",
            },
            {
                key: "codEstado",
                label: t("fields.state"),
                visible: true,
                sortable: true,
                width: "120px",
            },
        ],
        [t]
    );

    const estadosBadges = [
        { label: t("states.noData"), type: "info", Icon: CiClock1 },
        { label: t("states.noData"), type: "info", Icon: CiClock1 },
        { label: t("states.pending"), type: "info", Icon: CiClock1 },
        { label: t("states.incident"), type: "warning", Icon: CgDanger },
        { label: t("states.validated"), type: "success", Icon: CiCircleCheck },
        { label: t("states.closed"), type: "success", Icon: CiCircleCheck },
    ];

    const filterFields = [
        {
            key: "nombre",
            label: t("minorWorks.columns.centerCode"),
            type: "multiselect",
        },
        {
            key: "codEstado",
            label: t("fields.state"),
            type: "multiselect",
            labelMap: [
                t("states.noData"),
                t("states.noData"),
                t("states.pending"),
                t("states.incident"),
                t("states.validated"),
                t("states.closed"),
            ],
        },
        {
            key: "fechas",
            label: t("fields.endDate"),
            type: "daterange",
            rangeStartField: "fechaInicio",
            rangeEndField: "fechaFin",
        },
        {
            key: "fechasInicio",
            label: t("fields.startDate"),
            type: "daterange",
            rangeStartField: "fechaInicio",
            rangeEndField: "fechaInicio",
        },
        {
            key: "codZona",
            label: "Zona",
            type: "multiselect",
            labelMap: ZONE_LABEL_MAP,
        },
    ];

    const fetchObrasMenores = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/ObraMenor/GetObrasMenoresFromUser`
            );

            setObrasMenores(response.data);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las obras. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchUsuarios = async () => {
        const api = getApiInstance();
        try {
            let usuariosData = [];

            const response = await api.get(
                `/api/Usuario/GetUsuarios`
            );
            usuariosData = response.data;

            setUsuarios(
                mapList(
                    usuariosData.filter(
                        (u) => u.codIntermediario != null
                    ),
                    ["nombre", "apellidos"],
                    "codUsuario",
                    "codUsuario"
                )
            );
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los usuarios. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCadenas = async () => {
        const api = getApiInstance();
        try {
            let cadenasData = [];

            const response = await api.get(`/api/Cadenas/GetAllCadenas`);
            cadenasData = response.data;

            setCadenas([
                ...mapList(cadenasData, "cadena", "codCadena", "codCadena"),
            ]);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchComunidades = async () => {
        const api = getApiInstance();

        try {
            let comunidadesData = [];

            const response = await api.get(
                `/api/Comunidades/GetComunidades`
            );
            comunidadesData = response.data;

            setComunidades(
                mapList(
                    comunidadesData,
                    "comunidad",
                    "codComunidad",
                    "codComunidad",
                    "codPais"
                )
            );

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las comunidades. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchPaises = async () => {
        const api = getApiInstance();

        try {
            let paisesData = [];

            const response = await api.get(
                `/api/Paises/GetPaises`
            );
            paisesData = response.data;

            setPaises(mapList(paisesData, "pais", "codPais", "codPais"));
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los países. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchProvincias = async () => {
        const api = getApiInstance();
        try {
            let provinciasData = [];

            const response = await api.get(
                `/api/Provincias/GetProvincias`
            );
            provinciasData = response.data;

            setProvincias(
                mapList(
                    provinciasData,
                    "provincia",
                    "codProvincia",
                    "codProvincia",
                    "codComunidad"
                )
            );
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchProductores = async () => {
        const api = getApiInstance();
        try {
            let productoresData = [];

            const response = await api.get(
                `/api/Productores/GetProductores`
            );
            productoresData = response.data;

            setProductores([
                ...mapList(productoresData, "nombre", "codProductor"),
            ]);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const loadAll = async () => {
            try {
                await fetchObrasMenores();

                await Promise.all([
                    fetchUsuarios(),
                    fetchCadenas(),
                    fetchPaises(),
                    fetchComunidades(),
                    fetchProvincias(),
                    fetchProductores(),
                ]);
            } catch (err) {
                if (isMounted) {
                    setError(
                        "No se han podido cargar los datos. Vuelve a intentarlo más tarde."
                    );
                }
                console.error("Error en loadAll:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadAll();

        return () => {
            isMounted = false;
        };
    }, []);

    const [exportando, setExportando] = useState(false);

    const generateExcel = async ({ codsObrasMenores }) => {
        if (exportando) return;
        setExportando(true);
        const api = getApiInstance();

        try {
            const response = await api.post(
                "/api/ObraMenor/CreateReportByCods",
                codsObrasMenores,
                {
                    responseType: "blob",
                }
            );

            const fileName = nombreFicheroDescarga(
                response.headers["content-disposition"],
                "Informe_ObrasMenores.xlsx"
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(
                "No se pudo generar el reporte. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setExportando(false);
        }
    };

    const handleExportarSeleccionados = () => {
        let filasAExportar;

        if (selectedRows && selectedRows.size > 0) {
            // Si hay filas marcadas, exportar solo esas.
            filasAExportar = (obrasMenores ?? []).filter((o) =>
                [...selectedRows].some(
                    (id) => String(id) === String(o.codObraMenor ?? o.id)
                )
            );
        } else {
            // Si no hay seleccion, exportar las filas que muestra la tabla
            // (respetando los filtros aplicados, incluido el de Zona).
            filasAExportar = tableRef.current?.getVisibleRows() ?? [];
        }

        if (filasAExportar.length === 0) {
            setError(
                "No hay obras que exportar con los filtros aplicados."
            );
            return;
        }

        const codsObrasMenores = filasAExportar.map(
            (item) => item.codObraMenor ?? item.id
        );
        generateExcel({ codsObrasMenores });
    };

    return (
        <PageLayout
            title={t("minorWorks.title")}
            subtitle={t("minorWorks.subtitle")}
            actions={[
                ...(canAdd
                    ? [
                          <Button
                              key="add"
                              variant="filled"
                              startIcon={IoAdd({})}
                              onClick={() => tableRef.current?.openAddModal()}
                              endIcon={undefined}
                          >
                              {`${t("common.add")} ${t("minorWorks.title")}`}
                          </Button>,
                      ]
                    : []),
                <Button
                    key="export"
                    variant="filled"
                    startIcon={<FiDownload />}
                    onClick={handleExportarSeleccionados}
                    disabled={exportando}
                >
                    {exportando ? "Generando..." : t("common.export")}
                </Button>,
                ...(canAdd
                    ? [
                          <Button
                              key="import"
                              componentType="link"
                              variant="filled"
                              href="/"
                              startIcon={<FiDownload />}
                          >
                              {t("common.import")}
                          </Button>,
                      ]
                    : []),
            ]}
        >
            <div className="obras-menores-table">
                <Table
                    ref={tableRef}
                    hideAddButton={true}
                    columns={columnasObrasMenores}
                    dataTable={obrasMenores}
                    showCheckboxes={true}
                    selectedRows={selectedRows}
                    onSelectionChange={setSelectedRows}
                    expandable={false}
                    noResultsMessage={t("minorWorks.noWorksFound")}
                    actions={(row) => {
                        const enProceso = Number(row.codEstado) !== 5;
                        const acciones = [];
                        if (enProceso) {
                            acciones.push(t("common.edit"));
                            acciones.push({
                                label: t("common.close"),
                                icon: IoStopOutline({}),
                                action: async (r) => {
                                    try {
                                        const api = getApiInstance();
                                        await api.post(
                                            `/api/ObraMenor/CloseObraMenor/${r.codObraMenor}`
                                        );
                                        await fetchObrasMenores();
                                    } catch (err) {
                                        setError(
                                            "No se ha podido cerrar la obra menor. Vuelve a intentarlo más tarde."
                                        );
                                        console.error(err);
                                    }
                                },
                            });
                        }
                        acciones.push(t("common.delete"));
                        return acciones;
                    }}
                    searchTerms={["nombre", "localidad", "intermediario", "cadena", "codEstado"]}
                    keyProp="codObraMenor"
                    states={estadosBadges}
                    stateProp="codEstado"
                    elementName={t("minorWorks.title")}
                    sortProp="nombre"
                    filterFields={filterFields}
                    loading={loading}
                    formElements={[
                        {
                            title: t("sections.generalInfo"),
                            titleVisible: true,
                            elements: [
                                {
                                    name: "codProductor",
                                    type: "select",
                                    label: t("fields.producer"),
                                    placeholder: t("placeholders.selectProducer"),
                                    required: false,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                    options: productores,
                                },
                                {
                                    name: "nombre",
                                    type: "text",
                                    label: t("minorWorks.form.workCode"),
                                    placeholder: t("placeholders.enterWorkCode"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "fechaInicio",
                                    type: "date",
                                    label: t("fields.startDate"),
                                    placeholder: t("placeholders.selectDate"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "fechaFin",
                                    type: "date",
                                    label: t("fields.endDate"),
                                    placeholder: t("placeholders.selectDate"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "CodUsuario",
                                    type: "select",
                                    label: t("fields.intermediaryCompany"),
                                    placeholder: t(
                                        "placeholders.selectIntermediaryCompany"
                                    ),
                                    required: false,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                    options: usuarios,
                                },
                                {
                                    name: "codCadena",
                                    type: "select",
                                    label: t("fields.chain"),
                                    placeholder: t("placeholders.selectChain"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                    options: cadenas,
                                },
                                {
                                    name: "obs",
                                    type: "textarea",
                                    label: t("fields.observations"),
                                    placeholder: t(
                                        "minorWorks.form.observationsPlaceholder"
                                    ),
                                    required: false,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                            ],
                        },
                        {
                            title: t("sections.location"),
                            titleVisible: true,
                            elements: [
                                {
                                    name: "direccion",
                                    type: "text",
                                    label: t("fields.address"),
                                    placeholder: t("placeholders.enterAddress"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "codPais",
                                    type: "select",
                                    label: t("fields.country"),
                                    placeholder: t("placeholders.selectCountry"),
                                    options: paises,
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "codComunidad",
                                    type: "select",
                                    label: t("fields.community"),
                                    placeholder: t(
                                        "placeholders.selectCommunity"
                                    ),
                                    options: comunidades,
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                    associatedFormElement: "codPais",
                                },
                                {
                                    name: "codProvincia",
                                    type: "select",
                                    label: t("fields.province"),
                                    placeholder: t(
                                        "placeholders.selectProvince"
                                    ),
                                    options: provincias,
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                    associatedFormElement: "codComunidad",
                                },
                                {
                                    name: "localidad",
                                    type: "text",
                                    label: t("fields.locality"),
                                    placeholder: t(
                                        "placeholders.enterLocality"
                                    ),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "codZona",
                                    type: "number",
                                    label: "Zona",
                                    visible: false,
                                    defaultValue: selectedZone.value,
                                },
                            ],
                        },
                        {
                            title: t("minorWorks.form.generatedQuantities"),
                            titleVisible: true,
                            elements: [
                                {
                                    name: "cantidadRP",
                                    type: "number",
                                    label: t("minorWorks.form.dangerousWaste"),
                                    placeholder: t("placeholders.enterName"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "cantidadRNP",
                                    type: "number",
                                    label: t(
                                        "minorWorks.form.nonDangerousWaste"
                                    ),
                                    placeholder: t("placeholders.enterName"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                            ],
                        },
                    ]}
                    addApiUrl="/api/ObraMenor/SetObraMenor"
                    editApiUrl="/api/ObraMenor/UpdateObraMenor"
                    deleteApiUrl="/api/ObraMenor/DeleteObraMenor"
                    userCodAcceso={Number(user.codAcceso)}
                    userCodPerfil={Number(user.role)}
                    excludeFields={["intermediario", "cadena","fechaFin", "fechaInicio", "codProductor", "codObraMenor", "codCadena", "codProvincia", "codPais", "codComunidad", "codUsuario", "obs"]}
                />
            </div>
        </PageLayout>
    );
}
